// Shapes mirror Frontier's scanner-api (`scanner-api/server/api/openapi.yaml`), proxied through
// frontier-api at `frontier-api/frea/*`. tenantId/accountId/performedBy are filled in server-side.

/** scanner-api `sourceType`. */
export enum FreaRetrievalMethod {
  UPLOAD_FILE = 'UPLOAD_FILE',
  AWS_S3 = 'AWS_S3',
}

export type BucketStatus = 'PROCESSING' | 'DONE' | 'NOT_PROVISIONED';

export interface FreaBucketStatus {
  tenantId?: string;
  keyPrefix?: string;
  status: BucketStatus;
}

export enum FreaSourceStatus {
  CREATED = 'CREATED',
  CONNECTED = 'CONNECTED',
  FAILED = 'FAILED',
  ERROR = 'ERROR',
}

/** Per-file lifecycle reported by Frontier. */
export enum FreaFileStatus {
  INIT = 'INIT',
  PENDING = 'PENDING',
  FAILED = 'FAILED',
  SCANNED = 'SCANNED',
  DELETED = 'DELETED',
}

export interface FreaS3Config {
  awsAccount?: string;
  region?: string;
  bucket?: string;
  path?: string;
}

/** One row of the main table — a scanner-api source. */
export interface FreaSource {
  [key: string]: unknown;
  id: string;
  name: string;
  description?: string | null;
  sourceType: FreaRetrievalMethod;
  status: FreaSourceStatus;
  failureReason?: string | null;
  /** Rolling per-file retention age in days. Only for `UPLOAD_FILE`. */
  retentionDays?: number | null;
  s3Config?: FreaS3Config | null;
  fileCount: number;
  scannedCount: number;
  totalSizeBytes: number;
  totalTokensConsumed: number;
  lastStatusCheckAt?: string | null;
  lastUpdateAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One page of sources, most recently updated first; `total` counts sources across all pages. */
export interface FreaListSourcesResponse {
  sources: FreaSource[];
  total: number;
}

/** 1-based page, as used by the table's pagination. */
export interface FreaSourcesPagination {
  page: number;
  rowsPerPage: number;
}

export interface FreaFile {
  [key: string]: unknown;
  id: string;
  sourceId: string;
  key: string;
  fileName: string;
  size: number;
  uploadedBy: string;
  uploadedAt: string;
  status: FreaFileStatus;
  statusDetails?: string | null;
  tokensConsumed: number;
  retentionDays?: number | null;
  expiresAt?: string | null;
  binaryId?: string | null;
}

export interface FreaListFilesResponse {
  files: FreaFile[];
  continuationToken?: string | null;
}

export interface FreaArtifactHistoryEntry {
  id: string;
  sourceId: string;
  fileName: string;
  uploadedBy: string;
  uploadedAt: string;
  size: number;
  status: string;
  tokensConsumed: number;
  artifactName: string;
  tag?: string | null;
}

/** One week of the account's token usage. Weeks start on Sunday 00:00 UTC. */
export interface FreaWeeklyTokenStats {
  /** `yyyy-MM-dd`, the Sunday the week started. */
  weekStart: string;
  usedCredits: number;
  /** The customer's weekly limit for that week. Null until Frontier has a limit for the customer. */
  weeklyLimitCredits: number | null;
}

/** The current week's token usage of all accounts under the crmId. */
export interface FreaWeeklyQuota {
  /** `yyyy-MM-dd`. */
  weekStart: string;
  /** `yyyy-MM-dd`, the next Sunday. */
  resetsOn: string;
  usedCredits: number;
  /** Null until Frontier has a limit for the customer (no scan reserved yet). */
  weeklyLimitCredits: number | null;
}

export interface ConnectionFieldsValue {
  awsAccount: string;
  region: string;
  bucket: string;
  path: string;
  roleArn: string;
  externalId: string;
}

export interface CreateFreaSourceInput {
  name: string;
  description?: string;
  sourceType: FreaRetrievalMethod;
  retentionDays?: number;
  s3Config?: ConnectionFieldsValue;
}

// ── Multipart upload ─────────────────────────────────────────────────────────

export interface FreaInitiateMultipartResponse {
  id: string;
  uploadId: string;
  bucket: string;
  key: string;
}

export interface FreaPartUrlResponse {
  presignedUrl: string;
  partNumber: number;
}

export interface FreaCompletedPart {
  partNumber: number;
  eTag: string;
}

export type FileUploadStatus = 'QUEUED' | 'UPLOADING' | 'DONE' | 'ERROR';

export interface UploadingFile {
  id: string;
  file: File;
  progress: number;
  status: FileUploadStatus;
  secondsLeft?: number;
}
